import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Loader2, Download, FileSpreadsheet, Users, ClipboardList,
  FileText, Image as ImageIcon, Filter, X, Search, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import AdminHeader from './AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => {
  const token = localStorage.getItem('kaha_session');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TABS = [
  { id: 'orders', label: 'Ordenes', icon: ClipboardList },
  { id: 'customers', label: 'Clientes', icon: Users },
  { id: 'invoices', label: 'Facturas', icon: FileText },
  { id: 'artwork', label: 'Archivos', icon: ImageIcon },
];

const STATUS_LABELS = {
  pendiente_pago: 'Pendiente de pago', pago_confirmado: 'Pago confirmado',
  revision_archivo: 'Revisión archivo', aprobado_produccion: 'Aprobado producción',
  en_produccion: 'En producción', terminado: 'Terminado',
  listo_entrega: 'Listo entrega', cancelado: 'Cancelado',
};
const PAYMENT_LABELS = { pending: 'Pendiente', paid: 'Pagado', failed: 'Fallido', cancelled: 'Cancelado' };

const AdminDataExport = ({ adminUser }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [filterMaterial, setFilterMaterial] = useState('');
  const [filterName, setFilterName] = useState('');

  // Data
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [artworkFiles, setArtworkFiles] = useState([]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (filterStatus) params.set('customer_status', filterStatus);
      if (filterPayment) params.set('payment_status', filterPayment);
      if (filterMaterial) params.set('material_name', filterMaterial);
      if (filterName) params.set('customer_name', filterName);
      const res = await axios.get(`${API}/admin/orders/filtered?${params}`, { headers: getAuthHeaders() });
      setOrders(res.data);
    } catch { toast.error('Error al cargar ordenes'); }
    setLoading(false);
  }, [dateFrom, dateTo, filterStatus, filterPayment, filterMaterial, filterName]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterName ? `?search=${encodeURIComponent(filterName)}` : '';
      const res = await axios.get(`${API}/admin/customers${params}`, { headers: getAuthHeaders() });
      setCustomers(res.data);
    } catch { toast.error('Error al cargar clientes'); }
    setLoading(false);
  }, [filterName]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/invoices`, { headers: getAuthHeaders() });
      setInvoices(res.data);
    } catch { toast.error('Error al cargar facturas'); }
    setLoading(false);
  }, []);

  const fetchArtwork = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/artwork-files`, { headers: getAuthHeaders() });
      setArtworkFiles(res.data);
    } catch { toast.error('Error al cargar archivos'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'customers') fetchCustomers();
    else if (activeTab === 'invoices') fetchInvoices();
    else if (activeTab === 'artwork') fetchArtwork();
  }, [activeTab, fetchOrders, fetchCustomers, fetchInvoices, fetchArtwork]);

  const handleExportCSV = async (type) => {
    setExporting(type);
    try {
      const params = new URLSearchParams();
      if (type === 'orders') {
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);
        if (filterStatus) params.set('customer_status', filterStatus);
        if (filterPayment) params.set('payment_status', filterPayment);
      }
      const endpoint = type === 'orders' ? 'orders/export/csv' : type === 'customers' ? 'customers/export/csv' : 'invoices/export/csv';
      const res = await axios.get(`${API}/admin/${endpoint}?${params}`, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `KAHA-${type}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`CSV ${type} descargado`);
    } catch { toast.error('Error al exportar'); }
    setExporting(null);
  };

  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setFilterStatus(''); setFilterPayment(''); setFilterMaterial(''); setFilterName('');
  };

  const hasFilters = dateFrom || dateTo || filterStatus || filterPayment || filterMaterial || filterName;

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader adminUser={adminUser} currentPage="datos" />

      <main className="max-w-[1400px] mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b pb-3 overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        {(activeTab === 'orders' || activeTab === 'customers') && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros</span>
                {hasFilters && (
                  <button onClick={clearFilters} className="text-xs text-red-500 hover:underline ml-2 flex items-center gap-1">
                    <X className="w-3 h-3" /> Limpiar
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {activeTab === 'orders' && (
                  <>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Fecha desde</label>
                      <Input type="date" className="h-8 text-xs" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="filter-date-from" />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Fecha hasta</label>
                      <Input type="date" className="h-8 text-xs" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="filter-date-to" />
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Estado</label>
                      <Select value={filterStatus} onValueChange={v => setFilterStatus(v === '_all' ? '' : v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid="filter-order-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">Todos</SelectItem>
                          {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Pago</label>
                      <Select value={filterPayment} onValueChange={v => setFilterPayment(v === '_all' ? '' : v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid="filter-payment"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">Todos</SelectItem>
                          {Object.entries(PAYMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-1">Material</label>
                      <Input className="h-8 text-xs" placeholder="Ej: Sencillo" value={filterMaterial} onChange={e => setFilterMaterial(e.target.value)} data-testid="filter-material" />
                    </div>
                  </>
                )}
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-1">Cliente</label>
                  <Input className="h-8 text-xs" placeholder="Nombre o email" value={filterName} onChange={e => setFilterName(e.target.value)} data-testid="filter-name" />
                </div>
              </div>
              <div className="flex justify-end mt-3">
                <Button size="sm" className="h-8 text-xs gap-1.5 bg-accent hover:bg-accent/90"
                  onClick={() => activeTab === 'orders' ? fetchOrders() : fetchCustomers()}
                  data-testid="btn-apply-filters"
                >
                  <Search className="w-3.5 h-3.5" /> Buscar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export buttons */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">
            {activeTab === 'orders' && `${orders.length} ordenes`}
            {activeTab === 'customers' && `${customers.length} clientes`}
            {activeTab === 'invoices' && `${invoices.length} facturas`}
            {activeTab === 'artwork' && `${artworkFiles.length} archivos`}
          </p>
          {activeTab !== 'artwork' && (
            <Button size="sm" variant="outline" className="gap-1.5 text-xs"
              onClick={() => handleExportCSV(activeTab)}
              disabled={!!exporting}
              data-testid={`btn-export-${activeTab}-csv`}
            >
              {exporting === activeTab ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Exportar CSV
            </Button>
          )}
        </div>

        {/* Data Tables */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              {activeTab === 'orders' && <OrdersTable orders={orders} />}
              {activeTab === 'customers' && <CustomersTable customers={customers} />}
              {activeTab === 'invoices' && <InvoicesTable invoices={invoices} />}
              {activeTab === 'artwork' && <ArtworkTable files={artworkFiles} />}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

const OrdersTable = ({ orders }) => (
  <table className="w-full text-xs" data-testid="orders-data-table">
    <thead>
      <tr className="border-b bg-muted/50">
        {['Folio','Fecha','Cliente','Email','Teléfono','Material','Dimensiones','Qty','P/U','Total','Pago','Estado','Producción','Archivo'].map(h => (
          <th key={h} className="text-left p-2.5 font-medium whitespace-nowrap">{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {orders.length === 0 ? (
        <tr><td colSpan={14} className="text-center p-8 text-muted-foreground">Sin resultados</td></tr>
      ) : orders.map(o => {
        const dims = o.dimensions || {};
        const unitPrice = o.quantity > 0 ? (o.total_amount / o.quantity).toFixed(2) : '0';
        const artId = o.artwork_file_id || o.printing?.artwork_file_id;
        return (
          <tr key={o.id} className="border-b hover:bg-muted/20">
            <td className="p-2.5 font-mono font-bold">{o.order_number}</td>
            <td className="p-2.5 whitespace-nowrap">{o.created_at?.slice(0,10)}</td>
            <td className="p-2.5 max-w-[120px] truncate">{o.customer_name}</td>
            <td className="p-2.5 max-w-[140px] truncate">{o.customer_email}</td>
            <td className="p-2.5 whitespace-nowrap">{o.customer_phone}</td>
            <td className="p-2.5 whitespace-nowrap">{o.material?.name || '-'}</td>
            <td className="p-2.5 font-mono whitespace-nowrap">{dims.length}x{dims.width}x{dims.height}</td>
            <td className="p-2.5 text-right">{o.quantity}</td>
            <td className="p-2.5 text-right">${unitPrice}</td>
            <td className="p-2.5 text-right font-medium">${o.total_amount?.toLocaleString('es-MX', {minimumFractionDigits:2})}</td>
            <td className="p-2.5"><StatusBadge status={o.payment_status} labels={PAYMENT_LABELS} /></td>
            <td className="p-2.5"><StatusBadge status={o.customer_status} labels={STATUS_LABELS} /></td>
            <td className="p-2.5 whitespace-nowrap">{o.production_stage || '-'}</td>
            <td className="p-2.5">{artId ? <a href={`${API}/files/${artId}`} target="_blank" rel="noreferrer" className="text-accent hover:underline">Ver</a> : <span className="text-muted-foreground">-</span>}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const CustomersTable = ({ customers }) => (
  <table className="w-full text-xs" data-testid="customers-data-table">
    <thead>
      <tr className="border-b bg-muted/50">
        {['Nombre','Email','Teléfono','Empresa','RFC','Ciudad','Estado','CP','Pedidos','Total Gastado','Último Pedido'].map(h => (
          <th key={h} className="text-left p-2.5 font-medium whitespace-nowrap">{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {customers.length === 0 ? (
        <tr><td colSpan={11} className="text-center p-8 text-muted-foreground">Sin resultados</td></tr>
      ) : customers.map(c => (
        <tr key={c.email} className="border-b hover:bg-muted/20">
          <td className="p-2.5 font-medium">{c.name}</td>
          <td className="p-2.5">{c.email}</td>
          <td className="p-2.5">{c.phone || '-'}</td>
          <td className="p-2.5 max-w-[120px] truncate">{c.company || '-'}</td>
          <td className="p-2.5 font-mono">{c.rfc || '-'}</td>
          <td className="p-2.5">{c.city || '-'}</td>
          <td className="p-2.5">{c.state || '-'}</td>
          <td className="p-2.5">{c.postal_code || '-'}</td>
          <td className="p-2.5 text-center font-bold">{c.total_orders}</td>
          <td className="p-2.5 text-right font-medium">${c.total_spent?.toLocaleString('es-MX', {minimumFractionDigits:2})}</td>
          <td className="p-2.5 whitespace-nowrap">{c.last_order_date?.slice(0,10) || '-'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const InvoicesTable = ({ invoices }) => (
  <table className="w-full text-xs" data-testid="invoices-data-table">
    <thead>
      <tr className="border-b bg-muted/50">
        {['Folio','Fecha','Cliente','Razon Social','RFC','Total','Estado Factura','Estado Pago'].map(h => (
          <th key={h} className="text-left p-2.5 font-medium whitespace-nowrap">{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {invoices.length === 0 ? (
        <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">Sin facturas solicitadas</td></tr>
      ) : invoices.map(inv => (
        <tr key={inv.id} className="border-b hover:bg-muted/20">
          <td className="p-2.5 font-mono font-bold">{inv.order_number}</td>
          <td className="p-2.5 whitespace-nowrap">{inv.created_at?.slice(0,10)}</td>
          <td className="p-2.5">{inv.customer_name}</td>
          <td className="p-2.5">{inv.invoice_data?.name_or_company || inv.invoice_data?.razon_social || '-'}</td>
          <td className="p-2.5 font-mono">{inv.invoice_data?.rfc || '-'}</td>
          <td className="p-2.5 text-right font-medium">${inv.total_amount?.toLocaleString('es-MX', {minimumFractionDigits:2})}</td>
          <td className="p-2.5"><StatusBadge status={inv.invoice_status} labels={{pending:'Pendiente', generated:'Generada', sent:'Enviada', error:'Error'}} /></td>
          <td className="p-2.5"><StatusBadge status={inv.payment_status} labels={PAYMENT_LABELS} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ArtworkTable = ({ files }) => (
  <table className="w-full text-xs" data-testid="artwork-data-table">
    <thead>
      <tr className="border-b bg-muted/50">
        {['Archivo','Tipo','Tamaño','Fecha','Pedido','Cliente','Email','Descargar'].map(h => (
          <th key={h} className="text-left p-2.5 font-medium whitespace-nowrap">{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {files.length === 0 ? (
        <tr><td colSpan={8} className="text-center p-8 text-muted-foreground">Sin archivos</td></tr>
      ) : files.map(f => (
        <tr key={f.file_id} className="border-b hover:bg-muted/20">
          <td className="p-2.5 max-w-[180px] truncate font-medium">{f.filename}</td>
          <td className="p-2.5">{f.content_type?.split('/')[1] || '-'}</td>
          <td className="p-2.5 whitespace-nowrap">{(f.size / 1024).toFixed(0)} KB</td>
          <td className="p-2.5 whitespace-nowrap">{f.created_at?.slice(0,10)}</td>
          <td className="p-2.5 font-mono font-bold">{f.order_number || '-'}</td>
          <td className="p-2.5">{f.customer_name || '-'}</td>
          <td className="p-2.5">{f.customer_email || '-'}</td>
          <td className="p-2.5">
            <a href={`${API}/files/${f.file_id}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-accent hover:underline">
              <ExternalLink className="w-3 h-3" /> Ver
            </a>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800', paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800', cancelled: 'bg-gray-100 text-gray-600',
  pendiente_pago: 'bg-yellow-100 text-yellow-800', pago_confirmado: 'bg-blue-100 text-blue-800',
  revision_archivo: 'bg-purple-100 text-purple-800', aprobado_produccion: 'bg-indigo-100 text-indigo-800',
  en_produccion: 'bg-orange-100 text-orange-800', terminado: 'bg-green-200 text-green-900',
  listo_entrega: 'bg-green-100 text-green-800', cancelado: 'bg-gray-100 text-gray-600',
  generated: 'bg-green-100 text-green-800', sent: 'bg-blue-100 text-blue-800', error: 'bg-red-100 text-red-800',
};

const StatusBadge = ({ status, labels }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
    {labels?.[status] || status || '-'}
  </span>
);

export default AdminDataExport;
